import { __extends } from "tslib";
import { Vector3, Matrix, Quaternion, TmpVectors } from "../Maths/math.vector.js";
import { Camera } from "../Cameras/camera.js";
import { FreeCamera } from "../Cameras/freeCamera.js";
import { TargetCamera } from "../Cameras/targetCamera.js";
import { Viewport } from "../Maths/math.viewport.js";
import { Observable } from "../Misc/observable.js";
import { WebXRTrackingState } from "./webXRTypes.js";
/**
 * WebXR Camera which holds the views for the xrSession
 * @see https://doc.babylonjs.com/how_to/webxr_camera
 */
var WebXRCamera = /** @class */ (function (_super) {
    __extends(WebXRCamera, _super);
    /**
     * Creates a new webXRCamera, this should only be set at the camera after it has been updated by the xrSessionManager
     * @param name the name of the camera
     * @param scene the scene to add the camera to
     * @param _xrSessionManager a constructed xr session manager
     */
    function WebXRCamera(name, scene, _xrSessionManager) {
        var _this = _super.call(this, name, Vector3.Zero(), scene) || this;
        _this._xrSessionManager = _xrSessionManager;
        _this._firstFrame = false;
        _this._referenceQuaternion = Quaternion.Identity();
        _this._referencedPosition = new Vector3();
        _this._trackingState = WebXRTrackingState.NOT_TRACKING;
        /**
         * Observable raised before camera teleportation
         */
        _this.onBeforeCameraTeleport = new Observable();
        /**
         *  Observable raised after camera teleportation
         */
        _this.onAfterCameraTeleport = new Observable();
        /**
         * Notifies when the camera's tracking state has changed.
         * Notice - will also be triggered when tracking has started (at the beginning of the session)
         */
        _this.onTrackingStateChanged = new Observable();
        /**
         * Should position compensation execute on first frame.
         * This is used when copying the position from a native (non XR) camera
         */
        _this.compensateOnFirstFrame = true;
        _this._rotate180 = new Quaternion(0, 1, 0, 0);
        // Initial camera configuration
        _this.minZ = 0.1;
        _this.rotationQuaternion = new Quaternion();
        _this.cameraRigMode = Camera.RIG_MODE_CUSTOM;
        _this.updateUpVectorFromRotation = true;
        _this._updateNumberOfRigCameras(1);
        // freeze projection matrix, which will be copied later
        _this.freezeProjectionMatrix();
        _this._xrSessionManager.onXRSessionInit.add(function () {
            _this._referencedPosition.copyFromFloats(0, 0, 0);
            _this._referenceQuaternion.copyFromFloats(0, 0, 0, 1);
            // first frame - camera's y position should be 0 for the correct offset
            _this._firstFrame = _this.compensateOnFirstFrame;
        });
        // Check transformation changes on each frame. Callback is added to be first so that the transformation will be
        // applied to the rest of the elements using the referenceSpace object
        _this._xrSessionManager.onXRFrameObservable.add(function () {
            if (_this._firstFrame) {
                _this._updateFromXRSession();
            }
            _this._updateReferenceSpace();
            _this._updateFromXRSession();
        }, undefined, true);
        return _this;
    }
    Object.defineProperty(WebXRCamera.prototype, "trackingState", {
        /**
         * Get the current XR tracking state of the camera
         */
        get: function () {
            return this._trackingState;
        },
        enumerable: false,
        configurable: true
    });
    WebXRCamera.prototype._setTrackingState = function (newState) {
        if (this._trackingState !== newState) {
            this._trackingState = newState;
            this.onTrackingStateChanged.notifyObservers(newState);
        }
    };
    Object.defineProperty(WebXRCamera.prototype, "realWorldHeight", {
        /**
         * Return the user's height, unrelated to the current ground.
         * This will be the y position of this camera, when ground level is 0.
         */
        get: function () {
            var basePose = this._xrSessionManager.currentFrame && this._xrSessionManager.currentFrame.getViewerPose(this._xrSessionManager.baseReferenceSpace);
            if (basePose && basePose.transform) {
                return basePose.transform.position.y;
            }
            else {
                return 0;
            }
        },
        enumerable: false,
        configurable: true
    });
    /** @hidden */
    WebXRCamera.prototype._updateForDualEyeDebugging = function ( /*pupilDistance = 0.01*/) {
        // Create initial camera rigs
        this._updateNumberOfRigCameras(2);
        this.rigCameras[0].viewport = new Viewport(0, 0, 0.5, 1.0);
        // this.rigCameras[0].position.x = -pupilDistance / 2;
        this.rigCameras[0].outputRenderTarget = null;
        this.rigCameras[1].viewport = new Viewport(0.5, 0, 0.5, 1.0);
        // this.rigCameras[1].position.x = pupilDistance / 2;
        this.rigCameras[1].outputRenderTarget = null;
    };
    /**
     * Sets this camera's transformation based on a non-vr camera
     * @param otherCamera the non-vr camera to copy the transformation from
     * @param resetToBaseReferenceSpace should XR reset to the base reference space
     */
    WebXRCamera.prototype.setTransformationFromNonVRCamera = function (otherCamera, resetToBaseReferenceSpace) {
        if (otherCamera === void 0) { otherCamera = this.getScene().activeCamera; }
        if (resetToBaseReferenceSpace === void 0) { resetToBaseReferenceSpace = true; }
        if (!otherCamera || otherCamera === this) {
            return;
        }
        var mat = otherCamera.computeWorldMatrix();
        mat.decompose(undefined, this.rotationQuaternion, this.position);
        // set the ground level
        this.position.y = 0;
        Quaternion.FromEulerAnglesToRef(0, this.rotationQuaternion.toEulerAngles().y, 0, this.rotationQuaternion);
        this._firstFrame = true;
        if (resetToBaseReferenceSpace) {
            this._xrSessionManager.resetReferenceSpace();
        }
    };
    /**
     * Gets the current instance class name ("WebXRCamera").
     * @returns the class name
     */
    WebXRCamera.prototype.getClassName = function () {
        return "WebXRCamera";
    };
    WebXRCamera.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        this._lastXRViewerPose = undefined;
    };
    WebXRCamera.prototype._updateFromXRSession = function () {
        var _this = this;
        var pose = this._xrSessionManager.currentFrame && this._xrSessionManager.currentFrame.getViewerPose(this._xrSessionManager.referenceSpace);
        this._lastXRViewerPose = pose || undefined;
        if (!pose) {
            this._setTrackingState(WebXRTrackingState.NOT_TRACKING);
            return;
        }
        // Set the tracking state. if it didn't change it is a no-op
        var trackingState = pose.emulatedPosition ? WebXRTrackingState.TRACKING_LOST : WebXRTrackingState.TRACKING;
        this._setTrackingState(trackingState);
        // check min/max Z and update if not the same as in cache
        if (this.minZ !== this._cache.minZ || this.maxZ !== this._cache.maxZ) {
            var xrRenderState = {
                // if maxZ is 0 it should be "Infinity", but it doesn't work with the WebXR API. Setting to a large number.
                depthFar: this.maxZ || 10000,
                depthNear: this.minZ,
            };
            this._xrSessionManager.updateRenderState(xrRenderState);
            this._cache.minZ = this.minZ;
            this._cache.maxZ = this.maxZ;
        }
        if (pose.transform) {
            var orientation_1 = pose.transform.orientation;
            if (pose.transform.orientation.x === undefined) {
                // Babylon native polyfill can return an undefined orientation value
                // When not initialized
                return;
            }
            var pos = pose.transform.position;
            this._referencedPosition.set(pos.x, pos.y, pos.z);
            this._referenceQuaternion.set(orientation_1.x, orientation_1.y, orientation_1.z, orientation_1.w);
            if (!this._scene.useRightHandedSystem) {
                this._referencedPosition.z *= -1;
                this._referenceQuaternion.z *= -1;
                this._referenceQuaternion.w *= -1;
            }
            if (this._firstFrame) {
                this._firstFrame = false;
                // we have the XR reference, now use this to find the offset to get the camera to be
                // in the right position
                // set the height to correlate to the current height
                this.position.y += this._referencedPosition.y;
                // avoid using the head rotation on the first frame.
                this._referenceQuaternion.copyFromFloats(0, 0, 0, 1);
            }
            else {
                // update position and rotation as reference
                this.rotationQuaternion.copyFrom(this._referenceQuaternion);
                this.position.copyFrom(this._referencedPosition);
            }
        }
        // Update camera rigs
        if (this.rigCameras.length !== pose.views.length) {
            this._updateNumberOfRigCameras(pose.views.length);
        }
        pose.views.forEach(function (view, i) {
            var _a;
            var currentRig = _this.rigCameras[i];
            // update right and left, where applicable
            if (!currentRig.isLeftCamera && !currentRig.isRightCamera) {
                if (view.eye === "right") {
                    currentRig._isRightCamera = true;
                }
                else if (view.eye === "left") {
                    currentRig._isLeftCamera = true;
                }
            }
            // Update view/projection matrix
            var pos = view.transform.position;
            var orientation = view.transform.orientation;
            currentRig.parent = _this.parent;
            currentRig.position.set(pos.x, pos.y, pos.z);
            currentRig.rotationQuaternion.set(orientation.x, orientation.y, orientation.z, orientation.w);
            if (!_this._scene.useRightHandedSystem) {
                currentRig.position.z *= -1;
                currentRig.rotationQuaternion.z *= -1;
                currentRig.rotationQuaternion.w *= -1;
            }
            else {
                currentRig.rotationQuaternion.multiplyInPlace(_this._rotate180);
            }
            Matrix.FromFloat32ArrayToRefScaled(view.projectionMatrix, 0, 1, currentRig._projectionMatrix);
            if (!_this._scene.useRightHandedSystem) {
                currentRig._projectionMatrix.toggleProjectionMatrixHandInPlace();
            }
            // first camera?
            if (i === 0) {
                _this._projectionMatrix.copyFrom(currentRig._projectionMatrix);
            }
            var renderTargetTexture = _this._xrSessionManager.getRenderTargetTextureForView(view);
            _this._renderingMultiview = ((_a = renderTargetTexture === null || renderTargetTexture === void 0 ? void 0 : renderTargetTexture._texture) === null || _a === void 0 ? void 0 : _a.isMultiview) || false;
            if (_this._renderingMultiview) {
                // For multiview, the render target texture is the same per-view (just the slice index is different),
                // so we only need to set the output render target once for the rig parent.
                if (i == 0) {
                    _this._xrSessionManager.trySetViewportForView(_this.viewport, view);
                    _this.outputRenderTarget = renderTargetTexture;
                }
            }
            else {
                // Update viewport
                _this._xrSessionManager.trySetViewportForView(currentRig.viewport, view);
                // Set cameras to render to the session's render target
                currentRig.outputRenderTarget = renderTargetTexture || _this._xrSessionManager.getRenderTargetTextureForView(view);
            }
        });
    };
    WebXRCamera.prototype._updateNumberOfRigCameras = function (viewCount) {
        if (viewCount === void 0) { viewCount = 1; }
        while (this.rigCameras.length < viewCount) {
            var newCamera = new TargetCamera("XR-RigCamera: " + this.rigCameras.length, Vector3.Zero(), this.getScene());
            newCamera.minZ = 0.1;
            newCamera.rotationQuaternion = new Quaternion();
            newCamera.updateUpVectorFromRotation = true;
            newCamera.isRigCamera = true;
            newCamera.rigParent = this;
            // do not compute projection matrix, provided by XR
            newCamera.freezeProjectionMatrix();
            this.rigCameras.push(newCamera);
        }
        while (this.rigCameras.length > viewCount) {
            var removedCamera = this.rigCameras.pop();
            if (removedCamera) {
                removedCamera.dispose();
            }
        }
    };
    WebXRCamera.prototype._updateReferenceSpace = function () {
        // were position & rotation updated OUTSIDE of the xr update loop
        if (!this.position.equals(this._referencedPosition) || !this.rotationQuaternion.equals(this._referenceQuaternion)) {
            var referencedMat = TmpVectors.Matrix[0];
            var poseMat = TmpVectors.Matrix[1];
            var transformMat = TmpVectors.Matrix[2];
            Matrix.ComposeToRef(WebXRCamera._ScaleReadOnly, this._referenceQuaternion, this._referencedPosition, referencedMat);
            Matrix.ComposeToRef(WebXRCamera._ScaleReadOnly, this.rotationQuaternion, this.position, poseMat);
            referencedMat.invert().multiplyToRef(poseMat, transformMat);
            transformMat.invert();
            if (!this._scene.useRightHandedSystem) {
                transformMat.toggleModelMatrixHandInPlace();
            }
            transformMat.decompose(undefined, this._referenceQuaternion, this._referencedPosition);
            var transform = new XRRigidTransform({
                x: this._referencedPosition.x,
                y: this._referencedPosition.y,
                z: this._referencedPosition.z,
            }, {
                x: this._referenceQuaternion.x,
                y: this._referenceQuaternion.y,
                z: this._referenceQuaternion.z,
                w: this._referenceQuaternion.w,
            });
            this._xrSessionManager.referenceSpace = this._xrSessionManager.referenceSpace.getOffsetReferenceSpace(transform);
        }
    };
    WebXRCamera._ScaleReadOnly = Vector3.One();
    return WebXRCamera;
}(FreeCamera));
export { WebXRCamera };
//# sourceMappingURL=webXRCamera.js.map